import type { ActionButton } from '@/components/actionsPopup/types';
import {
  createActiveJobManagementButtons,
  createPreviewAsSitterButton,
  createReopenJobButton,
  createViewApplicantsButton,
} from '@/apps/seeker/jobs/helpers/jobActionButtons';

export type SeekerManageJob = {
  id: string;
  isActive?: boolean;
  status?: string;
  applications?: { totalCount?: number } | null;
  parent?: { id?: string } | null;
};

export type BuildSeekerManageJobActionsOptions = {
  job: SeekerManageJob | null | undefined;
  accountId: string | null | undefined;
  isFamilyConcierge?: boolean;
  onCloseJob?: () => void;
  onCopyJobLink?: () => void;
  /**
   * When true, force the first action to primary (open jobs list Manage Job UX).
   */
  emphasizeFirstAction?: boolean;
};

function isPendingStatus(status: string | undefined): boolean {
  return status === 'PENDING' || status === 'JOB_STATUS_PENDING';
}

/**
 * Builds Manage Job popup actions for parent seeker surfaces (open list, closed list, dashboard).
 * Shared jobs the account does not own only get View Applicants when applicable.
 */
export function buildSeekerManageJobActions(options: BuildSeekerManageJobActionsOptions): ActionButton[] {
  const { job, accountId, isFamilyConcierge = false, onCloseJob, onCopyJobLink, emphasizeFirstAction = false } = options;

  if (!job?.id) return [];

  const ownsJob = !!accountId && accountId === job.parent?.id;
  const hasApplicants = (job.applications?.totalCount ?? 0) > 0;
  const pending = isPendingStatus(job.status);
  // Pending jobs stay on the open/edit path even when isActive is false (dashboard behavior).
  const isOpenLike = !!job.isActive || pending;

  const buttons: ActionButton[] = [];

  // Pending jobs omit View Applicants (matches dashboard Manage Job behavior).
  if (hasApplicants && !pending) {
    buttons.push(
      createViewApplicantsButton(job.id, {
        variant: job.isActive ? 'primary' : 'outlined',
      })
    );
  }

  if (ownsJob) {
    if (isOpenLike) {
      if (onCloseJob && onCopyJobLink) {
        buttons.push(
          ...createActiveJobManagementButtons({
            jobId: job.id,
            onCloseJob,
            onCopyJobLink,
            isFamilyConcierge,
          })
        );
      }
    } else {
      buttons.push(createPreviewAsSitterButton(job.id), createReopenJobButton(job.id));
    }
  }

  if (emphasizeFirstAction && buttons.length > 0) {
    buttons[0] = { ...buttons[0], variant: 'primary' };
  }

  return buttons;
}
